import * as React from "react";

import {
  isMessagePathAttr,
  type MessagePathPart,
  type MessagePathSlice,
} from "@/shared/domain/topics";
import { TriangleAlert } from "@/shared/foundation/icons";
import { Banner } from "@/shared/foundation/primitives";
import type { PlotSeries } from "@/shared/state/visualization";
import { isSameDataSource } from "@/shared/state/visualization/identifiers";

import { useWorkspaceFiles } from "../../WorkspaceCtx/hooks";

import type { SeriesTruncation } from "./PlotDataStreamer";
import { seriesDisplayLabels } from "./seriesDisplayLabels";
import styles from "./SeriesTruncationNotice.module.css";

/**
 * The most series the alert names one sentence each. The alert lies over the
 * top of the chart and a sentence often wraps to two lines, so each one hides
 * data; past this many, one closing sentence counts the rest.
 */
const MAX_NAMED_SERIES = 3;

interface SeriesTruncationNoticeProps {
  /** The series the panel plots; each sentence names one by its label. */
  series: PlotSeries[];
  /**
   * What the last render reported as drawn only in part. One for a series not
   * in `series`, or for a data source the series no longer reads, is ignored.
   */
  truncations: SeriesTruncation[];
}

/**
 * Warns that a series' array has more elements than the plot draws, one
 * sentence per truncated series up to {@link MAX_NAMED_SERIES}, naming the
 * index a user can plot to reach the rest, then one sentence counting any
 * further series. Closing the alert dismisses every series it covers, named
 * or counted. Dismissal is component state per series and message path, so
 * the warning returns when the panel remounts or the series' message path
 * changes. A truncation reported for a data source the series no longer reads
 * is ignored, so a re-pointed series says nothing until its new read reports.
 */
export function SeriesTruncationNotice({
  series,
  truncations,
}: SeriesTruncationNoticeProps) {
  const workspaceFiles = useWorkspaceFiles();
  const [dismissed, setDismissed] = React.useState<Set<string>>(new Set());

  const seriesById = new Map(series.map((item) => [item.id, item]));
  const shown = truncations.flatMap((truncation) => {
    const plotted = seriesById.get(truncation.seriesId);
    // A truncation of a data source the series no longer reads is stale.
    return plotted === undefined ||
      !isSameDataSource(truncation.data, plotted.data) ||
      dismissed.has(dismissalKey(plotted))
      ? []
      : [{ truncation, series: plotted }];
  });
  if (shown.length === 0) {
    return null;
  }

  const labels = seriesDisplayLabels(series, workspaceFiles);
  const named = shown.slice(0, MAX_NAMED_SERIES);
  const counted = shown.length - named.length;

  return (
    <Banner
      variant="warning"
      role="alert"
      icon={<TriangleAlert aria-hidden />}
      onDismiss={() =>
        setDismissed(
          new Set([
            ...dismissed,
            ...shown.map((item) => dismissalKey(item.series)),
          ]),
        )
      }
    >
      <ul className={styles.list}>
        {named.map(({ truncation, series: plotted }) => {
          const field = wholeArrayField(plotted.data.messagePath.parts);
          return (
            <li key={truncation.seriesId}>
              {labels[truncation.seriesId]} has {truncation.elementCount}{" "}
              elements; showing the first {truncation.rendered}.
              {field !== undefined &&
                ` To see the rest, plot one element by index, for example ${field}[${truncation.rendered}].`}
            </li>
          );
        })}
        {counted > 0 && (
          <li>
            …and {counted} more series {counted === 1 ? "is" : "are"} not drawn
            in full.
          </li>
        )}
      </ul>
    </Banner>
  );
}

/** What a dismissal hides: this series plotting this message path. */
function dismissalKey(series: PlotSeries): string {
  return `${series.id}:${series.data.messagePath.id}`;
}

/**
 * The field to suggest an index on: the array a path takes whole, as the
 * `ranges` of `ranges[:]`. Undefined for every other shape — an offset or
 * bounded slice, or a slice earlier in the path — where the cut elements are
 * not one array's tail, so `field[n]` would name an element already drawn, or
 * none at all.
 */
function wholeArrayField(parts: MessagePathPart[]): string | undefined {
  const slices = parts.filter(
    (part): part is MessagePathSlice => !isMessagePathAttr(part),
  );
  const slice = slices[0];
  if (
    slices.length !== 1 ||
    slice !== parts.at(-1) ||
    slice.start !== 0 ||
    (slice.end ?? Infinity) !== Infinity
  ) {
    return undefined;
  }

  const field = parts.at(-2);
  return field !== undefined && isMessagePathAttr(field)
    ? field.attribute
    : undefined;
}
